import {drawStudentReceipt} from '../utils/studentReceipt';
import { studentFeeMonth } from '../utils/studentFeeMonths';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Ensure Axios points to the API server (adjust if you use a dev proxy)
if (!axios.defaults.baseURL) {
  axios.defaults.baseURL = 'http://localhost:5050';
}

// Helper to get YYYY-MM-DD in Asia/Kolkata timezone (IST)
function toYMDInTZ(d, tz = 'Asia/Kolkata') {
  if (!d) return '';
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = dtf.formatToParts(new Date(d));
  const y = parts.find(p => p.type === 'year')?.value || '';
  const m = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  return `${y}-${m}-${day}`;
}

// Build CSV safely
function csvEscape(val) {
  const s = String(val ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadReceiptForPayment(p,studentMap){
  const sid=typeof p.studentId==='object'?p.studentId?._id:p.studentId;
  const student=studentMap.get(String(sid))||(typeof p.studentId==='object'?p.studentId:{});
  const doc=new jsPDF({unit:'pt',format:'a4'});
  const info=drawStudentReceipt(doc,p,student||{});
  doc.save(`receipt_${info.organization.replace(/\s+/g,'')}_${info.studentCode==='Not recorded'?sid:info.studentCode}_${info.day}.pdf`);
}

function summarizePaymentTypes(payment) {
  if (!payment?.feeLines?.length) return '-';
  const parts = [];
  let lateFee = 0;

  for (const line of payment.feeLines) {
    switch (line.type) {
      case 'admissionFee':
        parts.push('Admission');
        break;
      case 'monthlyFee':
        parts.push(`Monthly (${studentFeeMonth(payment) || 'month unavailable'})`);
        break;
      case 'onetimeFee':
      case 'oneTimeFee':
      case 'onetimeCourseFee':
        parts.push('One-time Course');
        break;
      case 'eventFee': {
        // If backend populated event object, show its name; otherwise fall back to generic label
        const evName = (line && typeof line.eventId === 'object' && line.eventId?.name)
          ? line.eventId.name
          : (typeof line.eventName === 'string' && line.eventName.trim())
            ? line.eventName.trim()
            : '';
        parts.push(evName ? `Event (${evName})` : 'Event');
        break;
      }
      case 'miscFee':
        parts.push(line.note ? `Misc (${line.note})` : 'Misc');
        break;
      case 'lateFee':
        lateFee += Number(line.amount || 0);
        break;
      default:
        break;
    }
  }

  const unique = [...new Set(parts)];
  if (lateFee > 0) unique.push(`+ Late Rs.${lateFee}`);
  return unique.join(' + ');
}

// Generate a daily PDF and auto-save it (no print dialog)
function exportDailyPDF({ org = 'Salankaraa', dateYMD = '', payments = [], admissions = [] }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const padX = 40;

  // Format date in DD-MMM-YY (MMM uppercase)
  const fmt = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      const day = dt.toLocaleDateString('en-GB', { day: '2-digit', timeZone: 'Asia/Kolkata' });
      const month = dt.toLocaleDateString('en-GB', { month: 'short', timeZone: 'Asia/Kolkata' }).toUpperCase();
      const year = dt.toLocaleDateString('en-GB', { year: '2-digit', timeZone: 'Asia/Kolkata' });
      return `${day}-${month}-${year}`;
    } catch {
      return d;
    }
  };

  let dateLabel = '';
  if (dateYMD && typeof dateYMD === 'object' && dateYMD.from && dateYMD.to) {
    dateLabel = `${fmt(dateYMD.from)} to ${fmt(dateYMD.to)}`;
  } else {
    dateLabel = fmt(dateYMD);
  }

  const orgLine = (org === 'COG' || org === 'COG India Art Foundation')
   ? 'COG India Art Foundation'
   : (org === 'All' ? 'All Organizations' : (org || 'Salankaraa School of Performing Arts'));

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(orgLine, padX, 40);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('P384 Keyatala Lane, Kolkata - 700029', padX, 58);

  doc.setFontSize(12);
  doc.text(`Payment Report — ${dateLabel}`, padX, 82);

  const issued = new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata'
  }).format(new Date());
  doc.setFontSize(9);
  doc.text(`Generated: ${issued} (IST)`, padX, 98);

  // Payments Table - Add Teacher column
  const payCols = [
    { header: 'Date', dataKey: 'dateStr' },
    { header: 'Student / Customer', dataKey: 'student' },
    { header: 'Type', dataKey: 'type' },
    { header: 'Course', dataKey: 'course' },
    { header: 'Teacher', dataKey: 'teacher' },
    { header: 'Mode', dataKey: 'mode' },
    { header: 'Collected (INR)', dataKey: 'collected' },
  ];

  // Prepare payBody with teacher and organization for subtotal, now with invoice/collected only
  const payBody = payments.map(r => ({
    dateStr: fmt(r.dateStr || r.date || ''),
    student: r.student || '',
    type: r.type || '',
    course: r.course || '_',
    teacher: r.teacher || '-',
    mode: r.mode || '-',
    collected: (Number(r.collected || 0)).toLocaleString('en-IN'),
    _collectedNum: Number(r.collected || 0),
    organization: r.organization || '-',
  }));

  // Group rows by organization for subtotals
  const orgGroups = {};
  payBody.forEach(row => {
    const org = row.organization || '-';
    if (!orgGroups[org]) orgGroups[org] = [];
    orgGroups[org].push(row);
  });

  // Build body with subtotal rows per org (Collected only)
  const payBodyWithSubtotals = [];
  Object.entries(orgGroups).forEach(([org, rows]) => {
    rows.forEach(r => payBodyWithSubtotals.push(r));
    const subtotalCol = rows.reduce((sum, r) => sum + (r._collectedNum || 0), 0);
    payBodyWithSubtotals.push({
      dateStr: '', student: '', type: '', course: '', teacher: '', mode: `Subtotal (${org})`,
      collected: subtotalCol.toLocaleString('en-IN'),
      _isSubtotal: true,
    });
  });

  // Table rendering: bold subtotal rows, right align amount columns
  autoTable(doc, {
    head: [payCols.map(c => c.header)],
    body: payBodyWithSubtotals.map(r => payCols.map(c => r[c.dataKey])),
    startY: 120,
    styles: { fontSize: 9, cellPadding: 6, halign: 'left', valign: 'middle' },
    headStyles: { fillColor: [67, 56, 202], textColor: 255 },
    columnStyles: { 6: { halign: 'right' } },
    didParseCell: function (data) {
      // Bold subtotal rows
      const row = payBodyWithSubtotals[data.row.index];
      if (row && row._isSubtotal) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 245, 255];
      }
    },
  });

  // Grand total at end of payments table (Invoice, Collected)
  const pageW = doc.internal.pageSize.getWidth();
  const finalY = doc.lastAutoTable.finalY || 120;
  const grandCol = payBody.reduce((sum, r) => sum + (r._collectedNum || 0), 0);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total Collected: INR ${grandCol.toLocaleString('en-IN')}`, pageW - padX, finalY + 12, { align: 'right' });

  // Summary section: organization and mode grouping
  // Group by organization, then by mode
  const orgSummary = {};
  payBody.forEach(r => {
    const org = r.organization || '-';
    const mode = r.mode || '-';
    if (!orgSummary[org]) orgSummary[org] = { count: 0, sumCollected: 0, modes: {} };
    orgSummary[org].count += 1;
    orgSummary[org].sumCollected += r._collectedNum || 0;
    if (!orgSummary[org].modes[mode]) orgSummary[org].modes[mode] = { count: 0, sumCollected: 0 };
    orgSummary[org].modes[mode].count += 1;
    orgSummary[org].modes[mode].sumCollected += r._collectedNum || 0;
  });

  const summaryY = finalY + 32;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Summary', padX, summaryY);

  let currY = summaryY + 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  Object.entries(orgSummary).forEach(([org, { count, sumCollected, modes }]) => {
    doc.text(`${org}: ${count} payments, Collected INR ${sumCollected.toLocaleString('en-IN')}`, padX, currY);
    currY += 12;
    Object.entries(modes).forEach(([mode, { count: mCount, sumCollected: mSum }]) => {
      doc.text(`  ${mode}: ${mCount} payments, Collected INR ${mSum.toLocaleString('en-IN')}`, padX + 16, currY);
      currY += 12;
    });
    currY += 4;
  });

  // New Admissions section
  const y = currY + 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(`New Admissions (${dateLabel})`, padX, y);

  const admCols = [
    { header: 'Date', dataKey: 'date' },
    { header: 'ORG', dataKey: 'org' },
    { header: 'Student', dataKey: 'student' },
    { header: 'Course', dataKey: 'course' },
    { header: 'WhatsApp', dataKey: 'wa' },
  ];

  // Sort admissions by date ascending
  const sortedAdmissions = [...admissions].sort((a, b) => {
    const da = a?.date ? new Date(a.date).getTime() : 0;
    const db = b?.date ? new Date(b.date).getTime() : 0;
    return da - db;
  });

  const admBody = sortedAdmissions.map(a => ({
    date: fmt(a.date || ''),
    org: a.org === 'Salankaraa' ? 'SAL' : (a.org === 'COG' ? 'COG' : (a.org || '-')),
    student: a.student || '-',
    course: a.course || '_',
    wa: a.wa || '-',
  }));

  autoTable(doc, {
    head: [admCols.map(c => c.header)],
    body: admBody.map(r => admCols.map(c => r[c.dataKey])),
    startY: y + 10,
    styles: { fontSize: 9, cellPadding: 6, halign: 'left', valign: 'middle' },
    headStyles: { fillColor: [241, 245, 249], textColor: 0 },
  });

  // Filename: Org_Daily_YYYY-MM-DD_Day.pdf
  const safe = (s) => {
    if (!s) return '';
    if (typeof s === 'object') {
      if (s.from && s.to) return `${s.from}_to_${s.to}`.replace(/[^a-z0-9-_]+/gi, '_');
      return '';
    }
    return String(s).replace(/[^a-z0-9-_]+/gi, '_');
  };
  let weekday = '';
  try { weekday = new Date(dateYMD).toLocaleDateString('en-IN', { weekday: 'short' }); } catch {}
  const file = `${safe(org)}_Daily_${safe(dateYMD)}_${weekday || ''}.pdf`;
  doc.save(file);
}

export default function PaymentList() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [running, setRunning] = useState([]); // Running Sales (invoices)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [range, setRange] = useState('daily'); 
  const [fromDate, setFromDate] = useState(() => toYMDInTZ(new Date()));
  const [toDate, setToDate] = useState(() => toYMDInTZ(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => toYMDInTZ(new Date()));
  const [selectedOrg, setSelectedOrg] = useState('All');
  const [onlyAdmissionsView, setOnlyAdmissionsView] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editPayment, setEditPayment] = useState(null); // full payment object to edit
  const [editMode, setEditMode] = useState('UPI');      // paymentMode
  const [editDiscount, setEditDiscount] = useState(0);
  function normalizeFeeLinesForPut(feeLines = []) {
    return (feeLines || []).map(fl => {
      const out = {
        type: fl.type,
        amount: Number(fl.amount || 0)
      };
      if (fl.note) out.note = fl.note;

      // Preserve eventId / courseId as ObjectId strings if present
      if (fl.eventId) {
        if (typeof fl.eventId === 'object' && fl.eventId._id) out.eventId = fl.eventId._id;
        else out.eventId = fl.eventId;
      }
      if (fl.courseId) {
        if (typeof fl.courseId === 'object' && fl.courseId._id) out.courseId = fl.courseId._id;
        else out.courseId = fl.courseId;
      }
      return out;
    });
  }

  const openEdit = (payment) => {
    setEditPayment(payment);
    setEditMode(payment?.paymentMode || 'UPI');
    setEditDiscount(Number(payment?.discount || 0));
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editPayment?._id) return;
    try {
      // normalize fee lines first
      const normFeeLines = normalizeFeeLinesForPut(editPayment.feeLines || []);
      // compute total = sum(feeLines.amount) - discount
      const discountNum = Number(editDiscount || 0);
      const gross = normFeeLines.reduce((acc, fl) => acc + Number(fl.amount || 0), 0);
      const totalAmount = Math.max(gross - discountNum, 0);

      const sid = typeof editPayment.studentId === 'object'
        ? editPayment.studentId._id
        : editPayment.studentId;

      const payload = {
        studentId: sid,
        ...(editPayment.date ? { date: editPayment.date } : {}),
        ...(editPayment.feeLines.some(l=>l.type==='monthlyFee') ? { feeMonth: studentFeeMonth(editPayment) } : {}),             // keep original date
        paymentMode: editMode,
        discount: discountNum,
        feeLines: normFeeLines,
        totalAmount
      };

      await axios.put(`/api/payments/${editPayment._id}`, { ...payload, expectedVersion: editPayment.__v || 0 });

      // reload payments list
      const payRes = await axios.get('/api/payments');
      setPayments(Array.isArray(payRes.data) ? payRes.data : payRes.data?.payments || []);

      setEditOpen(false);
      setEditPayment(null);
    } catch (e) {
      const serverMsg = e?.response?.data?.error || e?.response?.data?.message;
      console.error('PUT /payments failed', serverMsg || e.message, e?.response?.data);
      alert(`Update failed: ${serverMsg || e.message}`);
    }
  };

  // Load base data once
  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      try {
        setLoading(true);
        setError('');
        const [payRes, stuRes, runRes] = await Promise.all([
          axios.get('/api/payments'),
          axios.get('/api/students'),
          axios.get('/api/running')
        ]);
        if (cancelled) return;
        setPayments(Array.isArray(payRes.data) ? payRes.data : payRes.data?.payments || []);
        setStudents(Array.isArray(stuRes.data) ? stuRes.data : []);
        setRunning(Array.isArray(runRes.data) ? runRes.data : []);
      } catch (err) {
        console.error('Failed to load payments/students', err);
        setError('Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAll();
    return () => { cancelled = true; };
  }, []);

  // Maps for quick lookups
  const studentMap = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(String(s._id), s);
    return map;
  }, [students]);

  const orgOptions = useMemo(() => {
   const set = new Set(['All']);
   set.add('Salankaraa'); // keep student org by default
   // Add student orgs
   students.forEach(s => {
     const org = s?.course?.organization;
     if (org) set.add(org);
   });
   // Add running-sale orgs (e.g., The Bengal Craft) so it shows up even if not in students
   running.forEach(r => {
     const org = r?.organization;
     if (org) set.add(org);
   });
   return Array.from(set);
 }, [students, running]);

  // Apply filters on the client for now
  const filtered = useMemo(() => {
    const ymd = selectedDate;
    return payments.filter(p => {
      const pd = toYMDInTZ(p?.date);
      if (range === 'daily') {
        if (pd !== ymd) return false;
      }
      if (range === 'range') {
        if (pd < fromDate || pd > toDate) return false;
      }

      // org filter
      if (selectedOrg !== 'All') {
        const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
        const stu = studentMap.get(String(sid));
        const org = stu?.course?.organization;
        if (org !== selectedOrg) return false;
      }
      return true;
    });
  }, [payments, range, selectedDate, fromDate, toDate, selectedOrg, studentMap]);

  const filteredRunning = useMemo(() => {
  const ymd = selectedDate;
  return running.filter(r => {
    const rd = toYMDInTZ(r?.date);
    if (range === 'daily') {
      if (rd !== ymd) return false;
    }
    if (range === 'range') {
      if (rd < fromDate || rd > toDate) return false;
    }
    if (selectedOrg !== 'All') {
      const org = r?.organization;
      if (org !== selectedOrg) return false;
    }
    return true;
  });
}, [running, range, selectedDate, fromDate, toDate, selectedOrg]);

  const filteredRunningPayments = useMemo(() => {
    const ymd = selectedDate;
    const rows = [];
    for (const r of running) {
      const org = r?.organization;
      const invNo = r?.invoiceNumber;
      const cust = r?.customerName || '-';
      const pmts = Array.isArray(r?.payments) ? r.payments : [];
      for (const pmt of pmts) {
        const pd = toYMDInTZ(pmt?.date);
        if (range === 'daily') {
          if (pd !== ymd) continue;
        }
        if (range === 'range') {
          if (pd < fromDate || pd > toDate) continue;
        }
        if (selectedOrg !== 'All' && org !== selectedOrg) continue;
        rows.push({ r, pmt, org, invNo, cust });
      }
    }
    return rows;
  }, [running, range, selectedDate, fromDate, toDate, selectedOrg]);


  const grandTotal = useMemo(() => {
  const a = filtered.reduce((sum, p) => sum + Number(p.totalAmount || 0), 0);
  const b = filteredRunning.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0);
  return a + b;
}, [filtered, filteredRunning]);

  

  const newAdmissionsSorted = useMemo(() => {
    const arr = filtered.filter(p => (p.feeLines || []).some(l => l.type === 'admissionFee'));
    return arr.sort((a, b) => {
      const sidA = typeof a.studentId === 'object' ? a.studentId?._id : a.studentId;
      const sidB = typeof b.studentId === 'object' ? b.studentId?._id : b.studentId;
      const sA = studentMap.get(String(sidA));
      const sB = studentMap.get(String(sidB));
      const orgA = (sA?.course?.organization || '').toLowerCase();
      const orgB = (sB?.course?.organization || '').toLowerCase();
      if (orgA < orgB) return -1; if (orgA > orgB) return 1;
      const nameA = (sA?.name || '').toLowerCase();
      const nameB = (sB?.name || '').toLowerCase();
      if (nameA < nameB) return -1; if (nameA > nameB) return 1;
      return 0;
    });
  }, [filtered, studentMap]);

  const handleExportCSV = () => {
    const rows = [];
    rows.push(['Date', 'Student', 'Payment Type', 'Course', 'Total Amount (Rs.)']);
    filtered.forEach(p => {
      const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
      const s = studentMap.get(String(sid));
      const name = (typeof p.studentId === 'object' ? p.studentId?.name : s?.name) || '-';
      const code = (typeof p.studentId === 'object' ? p.studentId?.studentId : s?.studentId) || '';
      const studentLabel = code ? `${name} (${code})` : name;
      const courseName = s?.course?.name || '_';
      const typeSummary = summarizePaymentTypes(p);
      const total = Number(p.totalAmount || 0);
      rows.push([selectedDate, studentLabel, typeSummary, courseName, total]);
    });
    rows.push(['', '', '', 'Aggregate Total', grandTotal]);

    const csv = rows.map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${selectedDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    // Build rows from current filtered list
    const paymentRows = onlyAdmissionsView ? [] : [
      // Student payments
      ...filtered.map(p => {
        const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
        const s = studentMap.get(String(sid));
        const name = (typeof p.studentId === 'object' ? p.studentId?.name : s?.name) || '-';
        const code = (typeof p.studentId === 'object' ? p.studentId?.studentId : s?.studentId) || '';
        const studentLabel = code ? `${name} (${code})` : name;
        const courseName = s?.course?.name || '_';
        const teacher = s?.course?.teacher || '-';
        const organization = s?.course?.organization || '-';
        const pDate = toYMDInTZ(p?.date);
        const typeSummary = summarizePaymentTypes(p);
        return {
          dateStr: pDate,
          student: studentLabel,
          type: typeSummary,
          course: courseName,
          teacher,
          mode: p?.paymentMode || '-',
    
           collected: Number(p.totalAmount || 0),
          _studentObj: s,
          organization,
        };
      }),
      // Running sales
       // Running payments (one row per payment on the selected day)
      ...filteredRunningPayments.map(({ r, pmt, org, invNo, cust }) => ({
        dateStr: toYMDInTZ(pmt?.date),
        student: `${cust}${invNo ? ` [${invNo}]` : ''}`,
        type: 'Running Payment',
        course: '-',
        teacher: '-',
        mode: pmt?.mode || r?.paymentMode || '-',
        collected: Number(pmt?.amount || 0),
        _studentObj: null,
        organization: org || '-',
      })),
    ];

    const admissions = newAdmissionsSorted.map(p => {
      const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
      const s = studentMap.get(String(sid));
      const name = (typeof p.studentId === 'object' ? p.studentId?.name : s?.name) || '-';
      const code = (typeof p.studentId === 'object' ? p.studentId?.studentId : s?.studentId) || '';
      const studentLabel = code ? `${name} (${code})` : name;
      return {
        date: toYMDInTZ(p?.date),
        org: s?.course?.organization || '-',
        student: studentLabel,
        course: s?.course?.name || '_',
        wa: s?.whatsapp || s?.phone || '-'
      };
    });

    const datePayload = range === 'range'
      ? { from: fromDate, to: toDate }
      : selectedDate;

    exportDailyPDF({
      org: selectedOrg,
      dateYMD: datePayload,
      payments: paymentRows,
      admissions
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-4">Payment Report</h2>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 bg-white p-4 rounded-lg shadow mb-6">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Range</label>
          <select value={range} onChange={e => setRange(e.target.value)} className="border rounded px-3 py-2">
  <option value="daily">Daily</option>
  <option value="range">Custom Range</option>
</select>
        </div>

        {range === 'daily' && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date</label>
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="border rounded px-3 py-2" />
          </div>
        )}
        {range === 'range' && (
  <>
    <div>
      <label className="block text-sm text-gray-600 mb-1">From</label>
      <input
        type="date"
        value={fromDate}
        onChange={e => setFromDate(e.target.value)}
        className="border rounded px-3 py-2"
      />
    </div>
    <div>
      <label className="block text-sm text-gray-600 mb-1">To</label>
      <input
        type="date"
        value={toDate}
        onChange={e => setToDate(e.target.value)}
        className="border rounded px-3 py-2"
      />
    </div>
  </>
)}

        <div>
          <label className="block text-sm text-gray-600 mb-1">Organization</label>
          <select value={selectedOrg} onChange={e => setSelectedOrg(e.target.value)} className="border rounded px-3 py-2">
            {orgOptions.map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
  <input
    type="checkbox"
    checked={onlyAdmissionsView}
    onChange={e => setOnlyAdmissionsView(e.target.checked)}
  />
  <label className="text-sm text-gray-700">
    Only Show Admission Block
  </label>
</div>

        <div className="ml-auto text-right">
          <div className="text-sm text-gray-500">Payment date</div>
          <div className="font-medium">{selectedDate}</div>
        </div>
        <div className="flex-1" />
        <div className="text-right">
          <div className="text-xs text-gray-500 mb-1">Times shown in Asia/Kolkata (IST)</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleExportCSV}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={handleExportPDF}
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded"
              title="Opens a printable view; use 'Save as PDF' in the dialog."
            >
            Export PDF
            </button>
          </div>
        </div>
      </div>

      {loading && <div>Loading…</div>}
      {error && <div className="text-red-600 mb-4">{error}</div>}

      {!loading && !error && (
        <>
        {!onlyAdmissionsView && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Student</th>
                  <th className="text-left px-4 py-2">Payment Type</th>
                  <th className="text-left px-4 py-2">Course</th>
                  <th className="text-left px-4 py-2">Teacher</th>
                  <th className="text-left px-4 py-2">WhatsApp</th>
                  <th className="text-left px-4 py-2">Mode</th>
                  
                  <th className="text-right px-4 py-2">Collected (INR)</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
  {(() => {
    // Build a combined list with a kind tag
    const combined = [
      ...filtered.map(p => ({
        kind: 'student',
        _org: (() => {
          const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
          const s = studentMap.get(String(sid));
          return s?.course?.organization || '-';
        })(),
        _data: p
      })),
...filteredRunningPayments.map(row => ({ kind: 'running', _org: row.org || '-', _data: row }))    ];

    // Group by organization
    const grouped = {};
    combined.forEach(row => {
      const org = row._org || '-';
      if (!grouped[org]) grouped[org] = [];
      grouped[org].push(row);
    });

    const rows = [];
    Object.entries(grouped).forEach(([org, list]) => {
      list.forEach(({ kind, _data }) => {
        if (kind === 'student') {
          const p = _data;
          const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
          const s = studentMap.get(String(sid));
          const name = (typeof p.studentId === 'object' ? p.studentId?.name : s?.name) || '-';
          const code = (typeof p.studentId === 'object' ? p.studentId?.studentId : s?.studentId) || '';
          const courseName = s?.course?.name || '_';
          const teacher = s?.course?.teacher || '-';
          const isAdmission = (p.feeLines || []).some(l => l.type === 'admissionFee');
          rows.push(
            <tr key={`p-${p._id}`} className="border-t">
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <span>{name} {code ? `(${code})` : ''}</span>
                  {isAdmission && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">New Admission</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-2">{summarizePaymentTypes(p)}</td>
              <td className="px-4 py-2">{courseName}</td>
              <td className="px-4 py-2">{teacher}</td>
              <td className="px-4 py-2">{s?.whatsapp || s?.phone || '-'}</td>
              <td className="px-4 py-2">{p?.paymentMode || '-'}</td>
              <td className="px-4 py-2 text-right">INR {Number(p.totalAmount || 0).toLocaleString('en-IN')}</td>
              <td className="px-4 py-2 text-right">
                <button type="button" onClick={() => openEdit(p)} className="text-indigo-600 hover:underline" title="Edit payment">Edit</button>
                <button type="button" onClick={() => downloadReceiptForPayment(p, studentMap)} className="ml-3 text-rose-600 hover:underline" title="Download receipt PDF">Receipt</button>
              </td>
            </tr>
          );
        } else {
          const { r, pmt, invNo, cust } = _data; // running payment row
          rows.push(
            <tr key={`r-${r._id}-${pmt._id || pmt.date}`} className="border-t">
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <span>{cust}</span>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">Running Payment</span>
                  {invNo && <span className="text-xs text-gray-500">[{invNo}]</span>}
                </div>
              </td>
              <td className="px-4 py-2">Running Payment</td>
              <td className="px-4 py-2">-</td>
              <td className="px-4 py-2">-</td>
              <td className="px-4 py-2">{r.whatsapp || '-'}</td>
              <td className="px-4 py-2">{pmt?.mode || r.paymentMode || '-'}</td>
              <td className="px-4 py-2 text-right">INR {Number(pmt?.amount || 0).toLocaleString('en-IN')}</td>
              <td className="px-4 py-2 text-right text-xs text-gray-500">—</td>
            </tr>
          );
        }
      });

      // Subtotal row per org (students + running combined)
      const subtotalCol = list.reduce((sum, row) => {
        if (row.kind === 'running') return sum + Number(row._data?.pmt?.amount || 0);
        return sum + Number(row._data?.totalAmount || 0);
      }, 0);
      rows.push(
        <tr key={`subtotal-${org}`} className="border-t bg-gray-100 font-semibold text-sm">
          <td className="px-4 py-2" colSpan={6}>{`Subtotal (${org})`}</td>
          <td className="px-4 py-2 text-right">INR {subtotalCol.toLocaleString('en-IN')}</td>
          <td className="px-4 py-2" />
        </tr>
      );
    });

    if (combined.length === 0) {
      return (
        <tr>
          <td colSpan={8} className="px-4 py-6 text-center text-gray-500">No payments for the selected filters.</td>
        </tr>
      );
    }
    return rows;
  })()}
</tbody>
              {(filtered.length > 0 || filteredRunningPayments.length > 0) && (
                <tfoot>
                  <tr className="bg-gray-50 border-t">
                    <td className="px-4 py-2 font-medium" colSpan={6}>Aggregate Total (Collected)</td>
                    <td className="px-4 py-2 text-right font-semibold">INR {(() => {
                      const a = filtered.reduce((sum, p) => sum + Number(p.totalAmount || 0), 0);
                      const b = filteredRunningPayments.reduce((sum, row) => sum + Number(row.pmt?.amount || 0), 0);
                      return (a + b).toLocaleString('en-IN');
                    })()}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

          {/* Summary section (mirrors PDF grouping) */}
          {!onlyAdmissionsView && filtered.length > 0 && (
  <div className="mt-6 text-sm bg-white rounded-lg shadow p-4">
              <div className="text-base font-semibold mb-2">Summary</div>
              {Object.entries(
                filtered.reduce((acc, p) => {
                  const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
                  const s = studentMap.get(String(sid));
                  const org = s?.course?.organization || '-';
                  const mode = p?.paymentMode || '-';
                  const amount = Number(p.totalAmount || 0);
                  if (!acc[org]) acc[org] = { count: 0, sum: 0, modes: {} };
                  acc[org].count += 1;
                  acc[org].sum += amount;
                  if (!acc[org].modes[mode]) acc[org].modes[mode] = { count: 0, sum: 0 };
                  acc[org].modes[mode].count += 1;
                  acc[org].modes[mode].sum += amount;
                  return acc;
                }, {})
              ).map(([org, { count, sum, modes }]) => (
                <div key={org} className="mb-2">
                  <div className="font-medium">{org}: {count} payments, INR {sum.toLocaleString('en-IN')}</div>
                  <ul className="pl-5 list-disc text-gray-700">
                    {Object.entries(modes).map(([mode, { count, sum }]) => (
                      <li key={mode}>{mode}: {count} payments, INR {sum.toLocaleString('en-IN')}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* New Admissions section */}
          <div className="mt-8 bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b font-medium">
              {(() => {
                const fmt = (d) => {
                  if (!d) return '';
                  try {
                    return new Date(d).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: '2-digit'
                    }).replace(/ /g, '-');
                  } catch {
                    return d;
                  }
                };

                const count = newAdmissionsSorted.length;

                if (range === 'daily') {
                  return `New Admissions (${fmt(selectedDate)}) — ${count}`;
                }

                return `New Admissions (${fmt(fromDate)} to ${fmt(toDate)}) — ${count}`;
              })()}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">ORG</th>
                  <th className="text-left px-4 py-2">Student</th>
                  <th className="text-left px-4 py-2">Course</th>
                  <th className="text-left px-4 py-2">WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {newAdmissionsSorted.map(p => {
                  const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
                  const s = studentMap.get(String(sid));
                  const name = (typeof p.studentId === 'object' ? p.studentId?.name : s?.name) || '-';
                  const code = (typeof p.studentId === 'object' ? p.studentId?.studentId : s?.studentId) || '';
                  const org = s?.course?.organization || '-';
                  return (
                    <tr key={`adm-${p._id}`} className="border-t">
                      <td className="px-4 py-2">{toYMDInTZ(p?.date)}</td>
                      <td className="px-4 py-2">
                        {org === 'Salankaraa' ? 'SAL' : org === 'COG' ? 'COG' : org}
                      </td>
                      <td className="px-4 py-2">{name} {code ? `(${code})` : ''}</td>
                      <td className="px-4 py-2">{s?.course?.name || '_'}</td>
                      <td className="px-4 py-2">{s?.whatsapp || s?.phone || '-'}</td>
                    </tr>
                  );
                })}
                {newAdmissionsSorted.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">No new admissions today.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Payment</h3>
              <button onClick={() => setEditOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Payment Mode</label>
                <select
                  value={editMode}
                  onChange={e => setEditMode(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option>UPI</option>
                  <option>Cash</option>
                  <option>Card</option>
                  <option>NetBanking</option>
                  <option>Others</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Discount</label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={editDiscount}
                  onChange={e => setEditDiscount(Number(e.target.value))}
                  min="0"
                />
              </div>

              <div className="text-xs text-gray-500">
                * This edit updates only the payment mode and discount. If you need to modify fee lines,
                use the dedicated Manage Payments page or delete and recreate the payment.
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                className="px-4 py-2 rounded border"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEdit}
                className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}